import React, { useState, useEffect } from 'react';
import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { Add } from '@mui/icons-material';
import { GridWrapper, StructureChildLocationAddGrid, StyledTextField, StyledTypography } from './ChildLocationsList.styles';
import { ChildLocationsDataType, ChildLocationsListProps } from './ChildLocationsList.props';
import { SelectMenu } from '../../atoms/SelectMenu';
import { TypographyVariantProps } from '../../atoms/Typography';
import { Button, ButtonVariantProps } from '../../atoms/Button';
import { ChildLocation } from '../../molecules/ChildLocation';
import { TextFieldTypeProps } from '../../atoms/TextField/TextField.props';
import { ENTER_LABEL, TAB, TAB_LABEL } from '../../../constants/common';
import { parseExcelData } from '../../../utils/common';

export const ChildLocationsList: React.FC<ChildLocationsListProps> = ({
  heading,
  structureTypes,
  childLocationsData,
  disableInputs,
  onChildLocationChange,
  onChildLocationCreate,
  onChildLocationDelete,
  onSmartInput,
  error
}) => {
  const [formData, setFormData] = useState<ChildLocationsDataType>(childLocationsData);
  const [smartInput, setSmartInput] = useState<string>('');

  useEffect(() => {
    childLocationsData && setFormData(childLocationsData);
  }, [childLocationsData]);
  
  const addNewForm = () => {
    const data = {
      name: '',
      reference1: '',
      reference2: '',
      reference3: ''
    };
    setFormData({ ...formData, childrenLocations:[ ...formData.childrenLocations, data ] });
    onChildLocationCreate({ ...formData, childrenLocations:[ ...formData.childrenLocations, data ] });
  };

  const onKeyDown = (event: any) => {
    const { value } = event.target;
    if (event.key === TAB_LABEL) {
      event.preventDefault();
  
      const cursorPosition = event.target.selectionStart;
      const cursorEndPosition = event.target.selectionEnd;
  
      event.target.value =
          value.substring(0, cursorPosition) +
          TAB +
          value.substring(cursorEndPosition);
  
      event.target.selectionStart = cursorPosition + 1;
      event.target.selectionEnd = cursorPosition + 1;
    } else if (event.key === ENTER_LABEL) {
      event.preventDefault();
  
      if (smartInput !== '') {
        const formatedChildLocationData = parseExcelData(smartInput || '') as Array<Array<string>>;
        if (formatedChildLocationData.length > 0) {
          const currentChildLocationData = formatedChildLocationData?.map((item: Array<string>, index: number) => {
            return {
              id: index + 1,
              name: item[0],
              reference1: item[1],
              reference2: item[2],
              reference3: item[3]
            };
          });

          let filteredLocations = [];
          const exisitingChildLocationsData = childLocationsData.childrenLocations;
          if (currentChildLocationData?.length > 0 && exisitingChildLocationsData?.length > 0) {
            const lastLocationId = exisitingChildLocationsData[exisitingChildLocationsData.length - 1]?.id || 0;
            let nextLocationId = lastLocationId + 1;
            if (
              exisitingChildLocationsData.length === 1 &&
              exisitingChildLocationsData[0].name === '' &&
              exisitingChildLocationsData[0].reference1 === '' &&
              exisitingChildLocationsData[0].reference2 === '' &&
              exisitingChildLocationsData[0].reference3 === ''
            ) {
              filteredLocations = currentChildLocationData;
            } else {
              filteredLocations = [
                ...exisitingChildLocationsData,
                ...currentChildLocationData.map((item: any) => ({
                  ...item,
                  id: nextLocationId++,
                })),
              ];
            }
          } else {
            filteredLocations = currentChildLocationData;
          }
          onSmartInput && onSmartInput({ ...childLocationsData, childrenLocations: filteredLocations });
        }
      }
      event.target.value = '';
    }
  };

  return (
    <GridWrapper>
      <Grid container>
        <Grid container justifyContent="space-between" alignItems="center" mx={1} flex={1}>
          <Grid>
            <StyledTypography variant={TypographyVariantProps.H6}>{heading}</StyledTypography>
          </Grid>
          <Grid ml={4}>
            <SelectMenu
              id="structure-type"
              label="Structure Type"
              required
              minWidth={220}
              items={structureTypes}
              selectedValue={formData?.selectedStructureType}
              onChange={(type: string) => onChildLocationChange({
                selectedStructureType: type,
                childrenLocations: [
                  {
                    name: '',
                    reference1: '',
                    reference2: '',
                    reference3: ''
                  }
                ]
              })}
              disabled={disableInputs}
            />
          </Grid>
          <Grid flex={1} px={2} mr={4}>
            <StyledTextField
              fullWidth
              multiline
              label="Smart Input"
              type={TextFieldTypeProps.String}
              onChange={(input: string) => setSmartInput(input)}
              onKeyDown={onKeyDown}
              disabled={!formData?.selectedStructureType || disableInputs}
            />
          </Grid>
          <StructureChildLocationAddGrid>
            <Button variant={ButtonVariantProps.Secondary} startIcon={<Add/>} onClick={addNewForm} disabled={disableInputs || !childLocationsData?.selectedStructureType}>
              Add
            </Button>
          </StructureChildLocationAddGrid>
        </Grid>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              {formData?.childrenLocations?.map((data,index) => 
                <TableRow key={index}>
                  <TableCell>
                    <Grid pt="5px">
                      <ChildLocation
                        value={data}
                        selectedStructureType={childLocationsData.selectedStructureType}
                        structureTypes={structureTypes}
                        disableInputs={disableInputs}
                        onChange={(updatedData) => {
                          const newData = formData.childrenLocations?.map((obj, i) => index === i ? updatedData : obj);
                          setFormData({ ...formData, childrenLocations: newData });
                          onChildLocationChange({ ...formData, childrenLocations: newData });
                        }}
                        onDelete={() => {
                          if (!formData?.selectedStructureType || disableInputs) return;
                          onChildLocationDelete(index);
                          const updatedData = [...formData.childrenLocations];                  
                          if (index > -1) {
                            updatedData.splice(index, 1);
                          }
                          setFormData({ ...formData, childrenLocations: [...updatedData] });
                        }}
                        error={error}
                      />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </GridWrapper>
  );
};